import "can/construct/";
import { setProduct, getAvailableProducts } from "../../helpers/products/products";
import MyUser from "src/models/my-user";
import MyAccount from "src/models/my-account";
import AccountProperties from "src/models/account-property";
import UserPermission from "src/components/shared/user-permissions";
import { SidebarMenu } from "./SidebarMenu";
import { pageLoader } from "./page-loader";
import { makeMenuOptions } from "./factory";
import { collectionHelper } from "src/helpers/object-property-finder";

import _find from "lodash/find";
import _some from "lodash/some";
import _filter from "lodash/filter";
import _forEach from "lodash/forEach";
import _toLower from "lodash/toLower";

export default can.Construct.extend({
    triggerAccess() {
        "use strict";

        can.when(
            AccountProperties.findSomeAccountPropertyNamesOf([
                "hos.locations.api.key",
                "COLOR_THEME",
                "hos.hidden.reports",
                "hos.hide.log.show.edits",
                "portal.external.css.url",
                "hos.carrier.information",
                "hos.disable.shorthaul.logging"
            ]),
            MyAccount.findAll({}),
            MyUser.findAll({})
        )
            .then((accountProperties, myAccount, myUser) => {
                const userPermissions = new UserPermission();
                const getAccountPropertyOf = collectionHelper(accountProperties, "name");

                const colorTheme = getAccountPropertyOf("COLOR_THEME");
                const hiddenReports = getAccountPropertyOf("hos.hidden.reports");
                const externalCSSUrl = getAccountPropertyOf("portal.external.css.url");
                const googleMapApiKey = getAccountPropertyOf("hos.locations.api.key");
                const hideLogShowEdits = getAccountPropertyOf("hos.hide.log.show.edits");
                const shorthaulLogging = getAccountPropertyOf("hos.disable.shorthaul.logging");
                const carrierInformation = getAccountPropertyOf("hos.carrier.information");

                // Reset the cache timezone every switching of an account
                if (Cookies.get("driversTZ")) {
                    Cookies.set("driversTZ", "", {
                        path: "/",
                        secure: true,
                        sameSite: "None"
                    });
                }

                // Keep the myAccount global variable updated
                Vistracks.myUser = myUser[0];
                Vistracks.myAccount = myAccount[0];

                Vistracks.myUser.attr(
                    "userRoles",
                    Cookies.get("userRoles") ? Cookies.get("userRoles").split(",") : []
                );

                // Hooks
                Vistracks.myAccount.attr("accountProperties", {
                    hosCarrierInformation: carrierInformation
                });

                if (shorthaulLogging) {
                    Vistracks.myAccount.attr(
                        "accountProperties.shorthaulLogging",
                        shorthaulLogging
                    );
                }

                const allowSubAccountBranding = (("allowSubAccountBranding" in Vistracks.myAccount) &&
                    Vistracks.myAccount.allowSubAccountBranding);

                let hasComplianceSupportCenterFeatures = false;
                if ("features" in myAccount[0]) {
                    if ("hasComplianceSupportCenterFeatures" in myAccount[0].features) {
                        hasComplianceSupportCenterFeatures = Vistracks.myAccount.features.hasComplianceSupportCenterFeatures;
                    }
                }

                // Notify the application state
                Vistracks.Scope.AppState.attr("allowSubAccountBranding",
                    Vistracks.myAccount.attr("allowSubAccountBranding"));

                Vistracks.Scope.AppState.attr("hasIftaFeatures", Vistracks.myAccount.attr("features.ifta") ? true : false);
                Vistracks.Scope.AppState.attr("hasMapFeatures", Vistracks.myAccount.attr("features.map") ? true : false);
                Vistracks.Scope.AppState.attr(
                    "hasEldFeatures",
                    Vistracks.myAccount.attr("features.eld") || Vistracks.myAccount.attr("features.logbook") ? true : false
                );
                Vistracks.Scope.AppState.attr("hasDvirFeatures", Vistracks.myAccount.attr("features.dvir") ? true : false);
                Vistracks.Scope.AppState.attr("hasWorkOrderFeatures", Vistracks.myAccount.attr("features.workOrder") ? true : false);
                Vistracks.Scope.AppState.attr("hasComplianceSupportCenterFeatures",
                    Vistracks.myAccount.attr("features.complianceSupportCenter") ? true : false);

                Vistracks.Scope.AppState.attr("isReseller", Vistracks.myAccount.attr("isReseller"));
                Vistracks.Scope.AppState.attr("isUserRoleAdmin", userPermissions.isUserRoleAdmin());
                Vistracks.Scope.AppState.attr("isUserRoleVTAdmin", userPermissions.get().PERM_IS_SYS_ADMIN);
                Vistracks.Scope.AppState.attr("canViewSubAccounts", userPermissions.canViewSubAccounts());
                Vistracks.Scope.AppState.attr("canReassignDrivingEvents", userPermissions.canReassignDrivingEvents());
                Vistracks.Scope.AppState.attr("googleMapApiKey", "");
                Vistracks.Scope.AppState.attr("hideFuelReceipts", true);
                Vistracks.Scope.AppState.attr("isNotDriver", true);

                // Notify the sidebar view model
                Vistracks.Scope.SidebarVM.attr("allowSubAccountBranding",
                    Vistracks.myAccount.attr("allowSubAccountBranding"));

                Vistracks.Scope.SidebarVM.attr("hasIftaFeatures", Vistracks.myAccount.attr("features.ifta") ? true : false);
                Vistracks.Scope.SidebarVM.attr("hasMapFeatures", Vistracks.myAccount.attr("features.map") ? true : false);
                Vistracks.Scope.SidebarVM.attr(
                    "hasEldFeatures",
                    Vistracks.myAccount.attr("features.eld") || Vistracks.myAccount.attr("features.logbook") ? true : false
                );
                Vistracks.Scope.SidebarVM.attr("hasDvirFeatures", Vistracks.myAccount.attr("features.dvir") ? true : false);
                Vistracks.Scope.SidebarVM.attr("hasWorkOrderFeatures", Vistracks.myAccount.attr("features.workOrder") ? true : false);
                Vistracks.Scope.SidebarVM.attr("hasComplianceSupportCenterFeatures",
                    Vistracks.myAccount.attr("features.complianceSupportCenter") ? true : false);

                Vistracks.Scope.SidebarVM.attr("isReseller", Vistracks.myAccount.attr("isReseller"));
                Vistracks.Scope.SidebarVM.attr("isUserRoleVTAdmin", userPermissions.get().PERM_IS_SYS_ADMIN);
                Vistracks.Scope.SidebarVM.attr("canReassignDrivingEvents", userPermissions.canReassignDrivingEvents());
                Vistracks.Scope.SidebarVM.attr("isUserRoleAdmin", userPermissions.isUserRoleAdmin());
                Vistracks.Scope.SidebarVM.attr("permissions", userPermissions.get());
                Vistracks.Scope.SidebarVM.attr("userPemissions", userPermissions);
                Vistracks.Scope.SidebarVM.attr("mobileWorker", false);
                Vistracks.Scope.SidebarVM.attr("hasProducts", false);
                Vistracks.Scope.SidebarVM.attr("isNotDriver", true);
                Vistracks.Scope.SidebarVM.attr("hideFuelReceipts", true);
                Vistracks.Scope.SidebarVM.attr("hasHOSReports", true);
                Vistracks.Scope.SidebarVM.attr("hasIFTAReports", true);
                Vistracks.Scope.SidebarVM.attr("hasWorkOrderReports", true);

                // Set Log Edits and Show Edits visibility
                Vistracks.Scope.AppState.attr("hideLogShowEdits", false);
                Vistracks.Scope.SidebarVM.attr("hideLogShowEdits", false);

                if (hideLogShowEdits) {
                    const isHide = hideLogShowEdits.value === "true";

                    Vistracks.Scope.AppState.attr("hideLogShowEdits", isHide);
                    Vistracks.Scope.SidebarVM.attr("hideLogShowEdits", isHide);
                }

                Vistracks.myProduct = getAvailableProducts(Vistracks.myAccount.features);

                Vistracks.myUserProducts = new can.List([]);
                _forEach(Vistracks.myUser.attr("enabledFeatures"), (enabledFeature, index) => {
                    const name = (enabledFeature === "WORK_ORDER") ? "Work Orders" : enabledFeature;
                    Vistracks.myUserProducts.push({
                        key: index,
                        name,
                        enabledFeature
                    });
                });

                const page = can.route.attr("page");
                const slug = can.route.attr("slug");

                Vistracks.Scope.$el.$body.removeClass(Vistracks.Scope.AppState.attr("themeColor"));

                // Set theme color
                if (colorTheme) {
                    Vistracks.Scope.$el.$body.addClass(colorTheme.value);
                    Vistracks.Scope.AppState.attr("themeColor", colorTheme.value);
                } else {
                    Vistracks.Scope.$el.$body.addClass("theme-blue");
                    Vistracks.Scope.AppState.attr("themeColor", "theme-blue");
                }

                if (hiddenReports) {
                    Vistracks.Scope.AppState.attr("hiddenReports", hiddenReports.value.trim().split(","));
                } else {
                    Vistracks.Scope.AppState.attr("hiddenReports", []);
                }

                Vistracks.Scope.SidebarVM.attr("hasHOSReports", Vistracks.HelperUtils.reports.hasReportsInGroup(
                    Vistracks.Scope.AppState.attr("hiddenReports"),
                    "Hours of Service"
                ));

                Vistracks.Scope.SidebarVM.attr("hasIFTAReports", Vistracks.HelperUtils.reports.hasReportsInGroup(
                    Vistracks.Scope.AppState.attr("hiddenReports"),
                    "State Mileage"
                ));

                Vistracks.Scope.SidebarVM.attr("hasWorkOrderReports", Vistracks.HelperUtils.reports.hasReportsInGroup(
                    Vistracks.Scope.AppState.hiddenReports,
                    "Mobile Worker"
                ));

                Vistracks.Scope.AppState.attr("googleMapApiKey", googleMapApiKey ? googleMapApiKey.value : "");

                // Enable the `Mobile Worker` menu if its enabled in the features.
                const mobileWorkerFeatureIsEnabled = ("workOrder" in Vistracks.myAccount.attr("features"))
                    && Vistracks.myAccount.attr("features.workOrder");

                if (Vistracks.myUser.enabledFeatures.length && mobileWorkerFeatureIsEnabled) {
                    Vistracks.Scope.SidebarVM.attr("mobileWorker", true);
                    Vistracks.Scope.AppState.attr("isMobileWorker", true);
                }

                // Determine the product(s) for the current active account
                if (Vistracks.myUser.enabledFeatures.length) {
                    Vistracks.Scope.AppState.attr("hasProducts", true);

                    const productSize = Vistracks.myUserProducts.length;

                    if (productSize) {
                        const setProducts = {};

                        _forEach(Vistracks.myUserProducts, (myUserProduct, index) => {
                            const enabledFeature = can.camelize(
                                _toLower(myUserProduct.attr("enabledFeature")).replace(/\_/g, "-")
                            );
                            const userProductInFeatures = (enabledFeature in Vistracks.myAccount.attr("features"));

                            if (userProductInFeatures && Vistracks.myAccount.attr(`features.${enabledFeature}`)) {
                                setProducts[myUserProduct.name] = true;
                            }

                            if (enabledFeature === 'eld' && Vistracks.myAccount.attr("features.logbook")) {
                                setProducts['LOGBOOK'] = true;
                            }

                            if ((index + 1) === productSize) {
                                setProduct(setProducts);
                            }
                        });
                    }
                }

                if (externalCSSUrl) {
                    if (can.trim(externalCSSUrl.value)) {
                        if ($('#externalcss').length == 0) {
                            let link = document.createElement('link');
                            link.setAttribute('id', 'externalcss');
                            link.setAttribute('type', 'text/css');
                            link.setAttribute('rel', 'stylesheet');
                            link.setAttribute('href', externalCSSUrl.value);
                            document.getElementsByTagName('head')[0].appendChild(link);
                        } else {
                            $('#externalcss').attr('href', externalCSSUrl.value);
                        }
                    } else {
                        if ($('#externalcss').length > 0) {
                            $('#externalcss').remove();
                        }
                    }
                } else {
                    if ($('#externalcss').length > 0) {
                        $('#externalcss').remove();
                    }
                }

                // Show/hide fuel receipts menu
                if (Vistracks.myUser.accountId === Vistracks.myAccount.id ||
                    Vistracks.Scope.AppState.permissions.PERM_IS_ACCOUNT_ADMIN ||
                    Vistracks.Scope.AppState.permissions.PERM_IS_SYS_ADMIN) {
                    Vistracks.Scope.AppState.attr("hideFuelReceipts", false);
                    Vistracks.Scope.SidebarVM.attr("hideFuelReceipts", false);
                }

                if (!Vistracks.myUser.enabledFeatures.length) {
                    can.route.attr({ page: page, slug: slug });
                    can.route.attr({ page: null, slug: null });
                    can.route.attr("page", "settings");
                    can.route.attr("slug", "accounts");
                } else {
                    Vistracks.Scope.SidebarVM.attr("hasProducts", true);
                    Vistracks.Scope.AppState.attr("hasProducts", true);
                }

                if (Vistracks.myUser.userRoles.length === 2) {
                    let isDriver = _filter(Vistracks.myUser.userRoles, item => {
                        return item === "USER_ROLE_DRIVER" || item === "PERM_EDIT_DRIVERS";
                    });

                    if (isDriver.length === 2) {
                        Vistracks.Scope.SidebarVM.attr("isNotDriver", false);
                        Vistracks.Scope.AppState.attr("isNotDriver", false);
                    }
                }

                // Account Logo loader
                let $body = $("body"),
                    $img = $body.find("img", "#company-logo"),
                    logoSrc = "/api/v2/branding/applicationLogo?image=header&" + new Date().getTime();

                Vistracks.Scope.SidebarVM["%root"]
                    .attr("accountLogo", logoSrc);

                // Reset condition everytime the user switches account.
                Vistracks.Scope.SidebarVM["%root"]
                    .attr("isAccountLogoError", false);

                // Handles image error
                $img.error(() => {
                    Vistracks.Scope.SidebarVM["%root"]
                        .attr("isAccountLogoError", true);
                });


                let host = window.location.hostname;
                let link = document.querySelector("link[rel*='icon']") || document.createElement('link');
                link.type = 'image/x-icon';
                link.rel = 'shortcut icon';
                link.href = logoSrc;
                document.getElementsByTagName('head')[0].appendChild(link);

                // Re-iniate sidebar menu controller
                setTimeout(() => {
                    VtAdmin.Modules
                        .leftSidebar
                        .init($body)
                        .activate();

                    $body = null;
                }, 400);

                // Clear cache
                $img = logoSrc = null;
                const menuOptions = makeMenuOptions(Vistracks.Scope.SidebarVM);
                const sidebarMenu = new SidebarMenu(menuOptions);

                setTimeout(() => {
                    Vistracks.Scope.SidebarVM.attr("menus", sidebarMenu.create());
                    pageLoader(Vistracks.myAccount);
                }, 50);
            });
    },

    bootstrap(vm) {
        "use strict";

        const appViewModel = vm["%root"];

        vm.attr("allowSubAccountBranding", appViewModel.attr("allowSubAccountBranding"));
        vm.attr("googleMapApiKey", appViewModel.attr("googleMapApiKey"));

        vm.attr("hasIftaFeatures", appViewModel.attr("hasIftaFeatures") ? true : false);
        vm.attr("hasMapFeatures", appViewModel.attr("hasMapFeatures") ? true : false);
        vm.attr("hasEldFeatures", appViewModel.attr("hasEldFeatures") ? true : false);
        vm.attr("hasDvirFeatures", appViewModel.attr("hasDvirFeatures") ? true : false);
        vm.attr("hasWorkOrderFeatures", appViewModel.attr("hasWorkOrderFeatures") ? true : false);
        vm.attr("hasComplianceSupportCenterFeatures", appViewModel.attr("hasComplianceSupportCenterFeatures") ? true : false);

        vm.attr("hasProducts", appViewModel.attr("hasProducts"));
        vm.attr("hideFuelReceipts", appViewModel.attr("hideFuelReceipts"));
        vm.attr("isReseller", appViewModel.attr("isReseller"));
        vm.attr("mobileWorker", appViewModel.attr("isMobileWorker"));
        vm.attr("isUserRoleAdmin", appViewModel.attr("isUserRoleAdmin") || false);
        vm.attr("isUserRoleVTAdmin", appViewModel.attr("permissions.PERM_IS_SYS_ADMIN") || false);
        vm.attr("canReassignDrivingEvents", appViewModel.attr("canReassignDrivingEvents"));
        vm.attr("isNotDriver", appViewModel.attr("isNotDriver"));
        vm.attr("permissions", appViewModel.attr("permissions"));

        vm.attr("hasHosPermission", appViewModel.attr("hasHosPermission") || false);
        vm.attr("hasIftaPermission", appViewModel.attr("hasIftaPermission") || false);
        vm.attr("hasDvirPermission", appViewModel.attr("hasDvirPermission") || false);
        vm.attr("hasMobileWorkerPermission", appViewModel.attr("hasMobileWorkerPermission") || false);

        vm.attr("products", appViewModel.attr("products"));

        vm.attr("hasHOSReports", Vistracks.HelperUtils.reports.hasReportsInGroup(
            appViewModel.attr("hiddenReports"),
            "Hours of Service"
        ));

        vm.attr("hasIFTAReports", Vistracks.HelperUtils.reports.hasReportsInGroup(
            appViewModel.attr("hiddenReports"),
            "State Mileage"
        ));

        vm.attr("hasWorkOrderReports", Vistracks.HelperUtils.reports.hasReportsInGroup(
            appViewModel.attr("hiddenReports"),
            "Mobile Worker"
        ));

        // Account Logo loader
        let $body = $("body"),
            $img = $body.find("img", "#company-logo"),
            logoSrc = "/api/v2/branding/applicationLogo?image=header&" + new Date().getTime();

        Vistracks.Scope.SidebarVM["%root"].attr("accountLogo", logoSrc);

        // Reset condition everytime the user switches account.
        Vistracks.Scope.SidebarVM["%root"].attr("isAccountLogoError", false);

        // Handles image error
        $img.error(() => Vistracks.Scope.SidebarVM["%root"].attr("isAccountLogoError", true));

        // Re-iniate sidebar menu controller
        let delayId = setTimeout(() => {
            VtAdmin.Modules.
                leftSidebar.
                init($body).
                activate();

            $body = null;

            clearTimeout(delayId);
        }, 400);

        // Clear cache
        $img = logoSrc = null;

        const menuOptions = makeMenuOptions(vm);
        const sidebarMenu = new SidebarMenu(menuOptions);
        vm.attr("menus", sidebarMenu.create());

        // This will set the router's default page to load base on account's product
        pageLoader(Vistracks.myAccount);
        const endTime = Date.now();
        const startTime = localStorage.getItem("startTimeLoginPage");
        console.log('Login Execution Time:' + (endTime - startTime) / 1000 + ' seconds');
        localStorage.removeItem("startTimeLoginPage");
    }
});
